/**
 * Location Selector Component
 *
 * Dropdown selector for organization and location switching
 * Only shown to users with appropriate permissions
 */

import React from 'react';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../context/AuthContext';
import { ChevronDown, Building2, MapPin } from 'lucide-react';
import styles from './LocationSelector.module.css';

const LocationSelector = () => {
  const { user, isDeveloper, selectedSiteGroup } = useAuth();
  const {
    selectedOrgId,
    selectedLocationId,
    setSelectedOrgId,
    setSelectedLocationId,
    availableOrgs,
    availableLocations,
    canSwitchLocations,
    canSwitchOrganizations,
    getLocationDisplayName,
    loading
  } = useLocation();

  // Don't show selector if user can't switch
  if (!canSwitchLocations() && !canSwitchOrganizations()) {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingText}>Loading locations...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Organization Selector (superuser only - developers use DeveloperOrgSelector) */}
      {canSwitchOrganizations() && availableOrgs.length > 1 && !isDeveloper && (
        <div className={styles.selectorGroup}>
          <Building2 size={16} className={styles.icon} />
          <select
            value={selectedOrgId || ''}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className={styles.selector}
            aria-label="Select Organization"
          >
            <option value="">All Organizations</option>
            {availableOrgs.map(org => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
          <ChevronDown size={14} className={styles.chevron} />
        </div>
      )}

      {/* Location Selector - Show for admins even with single location for context */}
      {canSwitchLocations() && availableLocations.length > 0 && (
        <div className={styles.selectorGroup}>
          <MapPin size={16} className={styles.icon} />
          {availableLocations.length > 1 ? (
            <select
              value={selectedLocationId || ''}
              onChange={(e) => setSelectedLocationId(e.target.value)}
              className={styles.selector}
              aria-label="Select Location"
            >
              {/* Owner, Admin, developer, and superuser see "All Locations" option */}
              {['owner', 'admin', 'developer', 'superuser'].includes(user?.role) && availableLocations.length > 1 &&
                !Array.isArray(user?.shareLocations) && !(selectedSiteGroup && selectedSiteGroup.orgId === user?.orgId) && (
                <option value="">All Locations</option>
              )}
              {availableLocations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          ) : (
            <span className={styles.singleLocation}>
              {availableLocations[0]?.name || 'Location'}
            </span>
          )}
          {availableLocations.length > 1 && <ChevronDown size={14} className={styles.chevron} />}
        </div>
      )}

      {/* Single location display (non-switchable) */}
      {!canSwitchLocations() && selectedLocationId && (
        <div className={styles.staticLocation}>
          <MapPin size={16} className={styles.icon} />
          <span>{getLocationDisplayName()}</span>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
